import { Badge, Card, CardContent, Skeleton, View } from "@/components/ui";
import { useEffect, useMemo } from "react";
import { CaretRight, SealPercent } from "@/components/shared/icons";
import { useInvoiceDetailStore } from "@/store/invoices/invoiceDetail";
import { formatPrice } from "@/lib/helpers/number";
import { useRoute } from "@/components/shared/router";
import { Typography } from "@/components/shared/typography";
import { useAvailableClientVouchersStore } from "@/store/invoices/useAvailableClientVouchers";
import { useAuthState } from "@/store/auth";
import { useRemoveVoucherInvoiceStore } from "@/store/invoices/removeVoucherInvoice";
import { VoucherDiscountTypeEnum } from "@/__generated__/graphql";
import { useShallow } from "zustand/react/shallow";
import { useInvoiceState } from "@/store/invoices";

export function AvailableVoucher({
  rightVariant = "caret",
}: {
  rightVariant?: "badge" | "caret";
}) {
  const { invoiceId, appliedVoucher, balance, fetchInvoiceDetail } =
    useInvoiceDetailStore(
      useShallow(({ fetch, data }) => ({
        fetchInvoiceDetail: fetch,
        invoiceId: data?.id as string,
        balance: data?.balance ?? 0,
        appliedVoucher: data?.appliedVoucher ?? null,
      })),
    );

  const setShowVoucherModal = useInvoiceState(
    (state) => state.setShowVoucherModal,
  );

  const {
    fetch: getVouchers,
    data,
    loading,
  } = useAvailableClientVouchersStore();

  const { fetch: removeVoucher, loading: loadingRemoveVoucher } =
    useRemoveVoucherInvoiceStore();

  const clientId = useAuthState((state) => state.data.userInfo.id);

  const { replace } = useRoute();

  useEffect(() => {
    if (clientId) {
      getVouchers({
        requestPayload: {
          clientId,
        },
      });
    }
  }, [clientId]);

  const numberVouchers = data?.length ?? 0;

  const showVoucherModal = () => {
    if (numberVouchers === 0) return;
    setShowVoucherModal(true);
  };

  const handleRemoveVoucher = async () => {
    const res = await removeVoucher({
      requestPayload: {
        input: {
          invoiceId,
        },
      },
    });
    if (!res.data) {
      return replace({
        pageKey: "invoices",
      });
    }
    fetchInvoiceDetail({
      requestPayload: {
        id: res.data.id,
      },
    });
    replace({
      pageKey: "invoiceDetail",
      params: {
        id: res.data.id,
      },
    });
    if (rightVariant === "badge") {
      showVoucherModal();
    }
  };

  const discountValue = useMemo(() => {
    let discount = appliedVoucher?.discountValue ?? 0;
    if (appliedVoucher?.discountType === VoucherDiscountTypeEnum.Percentage) {
      discount = (balance * appliedVoucher.discountValue) / 100;
    }

    return discount;
  }, [appliedVoucher]);

  const buttonTrigger = useMemo(() => {
    let button;
    if (rightVariant === "badge") {
      button = <Badge variant="info">Apply</Badge>;
    } else {
      button = (
        <>
          <Typography
            variant="label-sm"
            color="brand-primary"
            className="hidden md:block"
          >
            Apply Voucher
          </Typography>
          <CaretRight className="size-4" />
        </>
      );
    }
    return (
      <View
        className="flex cursor-pointer flex-row items-center gap-2"
        onClick={showVoucherModal}
      >
        {button}
      </View>
    );
  }, [rightVariant]);

  if (loading || loadingRemoveVoucher) {
    return <Skeleton className="h-6 w-full" />;
  }

  if (numberVouchers === 0 && !appliedVoucher) {
    return null;
  }

  return (
    <>
      <Card onClick={() => !appliedVoucher && showVoucherModal()}>
        <CardContent className="flex flex-row items-center justify-between gap-2 p-3">
          <SealPercent className="size-5 text-green-400" weight="fill" />
          {appliedVoucher ? (
            <View
              className="flex flex-1 cursor-pointer flex-row items-center gap-1"
              onClick={showVoucherModal}
            >
              <Typography variant="label-sm">
                Voucher applied. You saved
              </Typography>
              <Typography as="span" variant="label-sm" color="success">
                {formatPrice(discountValue, 0)}
              </Typography>
              <CaretRight className="size-4" />
            </View>
          ) : (
            <View className="flex-1">
              <Typography variant="body-sm">
                You have {numberVouchers} vouchers waiting for you!
              </Typography>
            </View>
          )}
          {appliedVoucher ? (
            <View onClick={handleRemoveVoucher}>
              {rightVariant === "badge" ? (
                <Badge variant="info">Change</Badge>
              ) : (
                <Typography variant="label-sm" color="danger">
                  Remove
                </Typography>
              )}
            </View>
          ) : (
            buttonTrigger
          )}
        </CardContent>
      </Card>
    </>
  );
}
